import { useState } from 'react';
import {
  Loader2, Search, AlertTriangle, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { diagnosticsAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

export function PropertySupplierProbePanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [rawOpen, setRawOpen] = useState(false);

  const runProbe = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await diagnosticsAPI.propertySupplierProbe();
      setData(res.data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Property Supplier Probe</CardTitle>
          <Button variant="outline" size="sm" onClick={runProbe} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Run probe
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Probes the BR OS API for a known no-supplier property (Kongensgade) and
          recursively searches the response for supplier-shaped fields, identifying
          whether supplier data lives at a different path than the sync code reads.
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Idle */}
        {!data && !error && !loading && (
          <p className="text-sm text-muted-foreground">
            Press Run probe to fetch the BR OS property response and scan for supplier fields.
          </p>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* API-level error from endpoint */}
        {data && (data.error || data.api_error) && (
          <div className="flex items-start gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p>{data.error || data.api_error}</p>
              {data.api_http_status != null && (
                <p className="text-xs text-muted-foreground mt-1">
                  HTTP status: {data.api_http_status}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Success */}
        {data && !data.error && !data.api_error && (
          <div className="space-y-4">
            {/* 1. Conclusion (prominent) */}
            <div className="rounded-lg border p-4 bg-muted/30">
              <p className="text-xs font-medium text-muted-foreground mb-1">Conclusion</p>
              <p className="text-sm font-semibold">{data.conclusion}</p>
            </div>

            {/* 2. Supplier field hits */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Supplier Field Hits</p>
              {data.api_supplier_fields && data.api_supplier_fields.length > 0 ? (
                <div className="overflow-x-auto border rounded-md">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/30 border-b">
                        <th className="px-2 py-1 text-left font-medium">Path</th>
                        <th className="px-2 py-1 text-left font-medium">Type</th>
                        <th className="px-2 py-1 text-left font-medium">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.api_supplier_fields.map((f, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{f.path}</td>
                          <td className="px-2 py-1 text-muted-foreground">{f.type}</td>
                          <td className="px-2 py-1 font-mono break-all max-w-md">
                            {typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-yellow-600 dark:text-yellow-400">
                  No supplier-shaped field found anywhere in the API response.
                </p>
              )}
            </div>

            {/* 3. Sync reads vs local */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Sync Reads (from API)</p>
                <p className="text-xs">
                  <span className="text-muted-foreground">supplier_id:</span>{' '}
                  <span className="font-mono">{data.sync_reads?.supplier_id ?? 'null'}</span>
                </p>
                <p className="text-xs">
                  <span className="text-muted-foreground">supplier_name:</span>{' '}
                  <span className="font-mono">{data.sync_reads?.supplier_name ?? 'null'}</span>
                </p>
                <p className="text-xs mt-1">
                  <span className="text-muted-foreground">Correct path:</span>{' '}
                  <span className={data.sync_reads_correct_path ? 'text-green-600' : 'text-red-500'}>
                    {data.sync_reads_correct_path ? 'yes' : 'no'}
                  </span>
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Local Supplier (stored)</p>
                <p className="text-xs">
                  <span className="text-muted-foreground">supplier_id:</span>{' '}
                  <span className="font-mono">{data.local_supplier?.supplier_id ?? 'null'}</span>
                </p>
                <p className="text-xs">
                  <span className="text-muted-foreground">supplier_name:</span>{' '}
                  <span className="font-mono">{data.local_supplier?.supplier_name ?? 'null'}</span>
                </p>
                {data.local_vs_api_mismatch != null && (
                  <p className="text-xs mt-1">
                    <span className="text-muted-foreground">Mismatch:</span>{' '}
                    <span className={data.local_vs_api_mismatch ? 'text-red-500 font-semibold' : 'text-green-600'}>
                      {data.local_vs_api_mismatch ? 'yes' : 'no'}
                    </span>
                  </p>
                )}
              </div>
            </div>

            {/* Property / booking identifiers + code locations */}
            <div className="rounded-lg border p-3 space-y-1 text-xs">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <span className="text-muted-foreground">
                  Property: <span className="font-mono font-medium text-foreground">{data.property_name || data.property_id}</span>
                  {data.property_name && (
                    <span className="ml-1 text-muted-foreground">({data.property_id})</span>
                  )}
                </span>
                {data.booking_reference && (
                  <span className="text-muted-foreground">
                    Booking: <span className="font-mono font-medium text-foreground">{data.booking_reference}</span>
                  </span>
                )}
                {data.headline_total != null && (
                  <span className="text-muted-foreground">
                    Headline total: <span className="font-mono font-medium text-foreground">{data.headline_total}</span>
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground">
                {data.api_http_status != null && (
                  <span>API status: <span className="font-mono">{data.api_http_status}</span></span>
                )}
                {data.sync_code_location && (
                  <span>Sync code: <span className="font-mono">{data.sync_code_location}</span></span>
                )}
                {data.resolver_code_location && (
                  <span>Resolver: <span className="font-mono">{data.resolver_code_location}</span></span>
                )}
              </div>
            </div>

            {/* 4. Raw API response (collapsible) */}
            {data.api_full_structure && (
              <div className="border rounded-md">
                <button
                  onClick={() => setRawOpen(!rawOpen)}
                  className="flex items-center gap-1 w-full px-3 py-1.5 text-xs font-medium text-left hover:bg-muted/50"
                >
                  {rawOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  Full API structure ({data.api_full_structure.length} fields)
                </button>
                {rawOpen && (
                  <div className="overflow-x-auto border-t max-h-96 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-background">
                        <tr className="bg-muted/30 border-b">
                          <th className="px-2 py-1 text-left font-medium">Path</th>
                          <th className="px-2 py-1 text-left font-medium">Type</th>
                          <th className="px-2 py-1 text-left font-medium">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.api_full_structure.map((f, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1 font-mono whitespace-nowrap">{f.path}</td>
                            <td className="px-2 py-1 text-muted-foreground">{f.type}</td>
                            <td className="px-2 py-1 font-mono break-all max-w-md">
                              {typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
